import { useEffect, useState } from "react";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dumbbell, Bike, Waves, Activity, Sparkles, Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, TrendingUp, HeartPulse, ThumbsUp, ThumbsDown, ShieldAlert } from "lucide-react";

const ACTIVITIES = [
  { key: "renforcement", label: "Renforcement musculaire", Icon: Dumbbell },
  { key: "velo", label: "Vélo", Icon: Bike },
  { key: "natation", label: "Natation", Icon: Waves },
  { key: "cross_training", label: "Cross-training", Icon: Activity },
  { key: "autre", label: "Autre activité", Icon: Sparkles },
];

const VERDICT = {
  recommande: { label: "Recommandé", cls: "bg-sage/15 text-sage border-sage/30", Icon: CheckCircle2 },
  prudence: { label: "Avec prudence", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30", Icon: AlertTriangle },
  deconseille: { label: "Déconseillé cette semaine", cls: "bg-destructive/10 text-destructive border-destructive/30", Icon: XCircle },
};

export default function ComplementarySports() {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { setResult(null); }, [selected]);

  const analyze = async () => {
    if (!selected) { toast.error("Choisis une activité"); return; }
    if (selected === "autre" && !note.trim()) { toast.error("Précise l'activité"); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/complementary/advice", { activity: selected, note: note.trim() || null });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse indisponible, réessaie dans un instant");
    } finally { setLoading(false); }
  };

  const v = result ? (VERDICT[result.verdict] || VERDICT.prudence) : null;

  return (
    <ClientLayout>
      <div className="max-w-2xl mx-auto pb-8" data-testid="complementary-page">
        <div className="fade-up-1">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-semibold text-sage">
            <Sparkles size={14} /> Conseil IA
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-medium leading-tight">Sports complémentaires</h1>
          <p className="mt-3 text-base text-obsidian/60">
            L'IA n'ajoute pas de séance : elle analyse ta charge running, ta fatigue et ton historique récent pour te dire
            si — et quand — une activité complémentaire est judicieuse cette semaine.
          </p>
        </div>

        {/* Sélecteur d'activité */}
        <div className="fade-up-2 mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {ACTIVITIES.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`activity-${key}`}
              onClick={() => setSelected(key)}
              className={`rounded-2xl border p-4 text-left transition-[background-color,border-color,transform] active:scale-[0.98] ${selected === key ? "border-sage bg-sage/10 shadow-premium" : "border-border bg-white hover:border-sage/40"}`}
            >
              <Icon size={22} strokeWidth={1.6} className={selected === key ? "text-sage" : "text-obsidian/50"} />
              <div className="mt-2 text-sm font-medium leading-tight">{label}</div>
            </button>
          ))}
        </div>

        {/* Note / précision */}
        <div className="fade-up-3 mt-4">
          <textarea
            data-testid="complementary-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={selected === "autre" ? "Quelle activité ? (ex. yoga, escalade…) et comment tu te sens" : "Optionnel : comment te sens-tu ? contraintes de la semaine ?"}
            rows={3}
            className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-base resize-none focus:outline-none focus:border-sage"
          />
        </div>

        <button
          data-testid="complementary-analyze"
          onClick={analyze}
          disabled={loading || !selected}
          className="fade-up-4 mt-4 w-full rounded-[28px] bg-obsidian text-white py-4 text-base font-medium inline-flex items-center justify-center gap-2 hover:bg-obsidian/90 transition-[background-color] disabled:opacity-50"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Analyse en cours…</> : <><Sparkles size={18} /> Analyser ma situation</>}
        </button>

        {/* Résultat */}
        {result && v && (
          <div data-testid="complementary-result" className="mt-6 space-y-4 fade-up-1">
            <div className={`rounded-2xl border p-5 ${v.cls}`}>
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                <v.Icon size={18} /> {v.label}
              </div>
              <div className="mt-1 text-lg font-medium text-obsidian">{result.titre_verdict}</div>
              <div className="mt-1 text-sm text-obsidian/60">
                {result.activite} · Semaine {result.charge_resume?.semaine ?? "—"} · {result.charge_resume?.realisees ?? 0}/{result.charge_resume?.seances_prevues ?? 0} séances faites
              </div>
            </div>

            <Card Icon={Activity} title="Analyse de ta situation" data-testid="result-analyse">{result.analyse}</Card>
            <Card Icon={Clock} title="Quand cette semaine ?" data-testid="result-quand">{result.quand}</Card>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card Icon={HeartPulse} title="Impact récupération">{result.impact_recuperation}</Card>
              <Card Icon={TrendingUp} title="Impact progression">{result.impact_progression}</Card>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <ListCard Icon={ThumbsUp} title="Bénéfique si" items={result.benefique_si} tone="sage" />
              <ListCard Icon={ThumbsDown} title="Déconseillé si" items={result.deconseille_si} tone="destructive" />
            </div>

            {result.intensite_conseillee && (
              <Card Icon={Sparkles} title="Intensité conseillée">{result.intensite_conseillee}</Card>
            )}

            {result.disclaimer && (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 flex gap-2 items-start">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" /> {result.disclaimer}
              </div>
            )}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}

function Card({ Icon, title, children, ...rest }) {
  if (!children) return null;
  return (
    <div className="rounded-2xl border border-border bg-white p-5" {...rest}>
      <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">
        <Icon size={15} strokeWidth={1.7} /> {title}
      </div>
      <p className="mt-2 text-base text-obsidian/80 leading-relaxed">{children}</p>
    </div>
  );
}

function ListCard({ Icon, title, items, tone }) {
  if (!items || items.length === 0) return null;
  const dot = tone === "sage" ? "text-sage" : "text-destructive";
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className={`flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-semibold ${dot}`}>
        <Icon size={15} strokeWidth={1.7} /> {title}
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-obsidian/80 flex gap-2"><span className={dot}>—</span>{it}</li>
        ))}
      </ul>
    </div>
  );
}
